import { motion } from "framer-motion";
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import type { AnimationItem } from "lottie-web";
import loadingAnimation from "./animate.json";
import clsx from "clsx";
import { BackIcon } from "@/components/RFIcon";

interface Props {
	page: number;
	totalPage: number;
	direction: "forward" | "backward";
	style?: React.CSSProperties;
	showBackIcon?: boolean;
	onBackClick?: () => void;
}

export type SurveyHeaderProgressBarInstance = {
	onComplete: () => Promise<boolean>;
};
const SurveyHeaderProgressBar = forwardRef<SurveyHeaderProgressBarInstance, Props>((props, ref) => {
	const { page, totalPage, direction } = props;
	const easeConfig = [0.25, 0.1, 0.25, 1.0];

	const lottieRef = useRef<HTMLDivElement>(null);
	const animationInstance = useRef<AnimationItem | null>(null);
	const [displayAnimation, setDisplayAnimation] = useState(false);
	const [isComplete, setIsComplete] = useState(false);

	useEffect(() => {
		let cancelled = false;
		import("lottie-web").then((lottie) => {
			if (cancelled) return;
			if (animationInstance.current) {
				animationInstance.current.destroy();
			}
			if (!lottieRef.current) return;
			animationInstance.current = lottie.default.loadAnimation({
				loop: false,
				autoplay: false,
				animationData: loadingAnimation,
				container: lottieRef.current as HTMLDivElement,
				renderer: "svg",
			});

			animationInstance.current.addEventListener("complete", () => {
				setDisplayAnimation(false);
			});
		});
		return () => {
			cancelled = true;
			animationInstance.current?.destroy();
			animationInstance.current = null;
		};
	}, []);

	const playAnimation = () => {
		setDisplayAnimation(true);
		if (animationInstance.current) {
			animationInstance.current.goToAndPlay(0, false);
		}
	};

	useEffect(() => {
		if (direction === "backward") return;
		playAnimation();
	}, [page]);

	const progress = useMemo(() => {
		return (page / totalPage) * 100;
	}, [page, totalPage]);

	useImperativeHandle(ref, () => ({
		onComplete: async () => {
			setIsComplete(true);
			return new Promise((resolve) => {
				setTimeout(() => {
					resolve(true);
				}, 1500);
			});
		},
	}));

	return (
		<div className="px-4.5 flex items-center space-x-4 pt-[2.4vh] md:pt-[8vh]">
			{props.showBackIcon && page > 1 && (
				<BackIcon className="flex-shrink-0 cursor-pointer" onClick={props.onBackClick} />
			)}
			<motion.div
				className="relative rounded-xl flex-1"
				animate={{
					boxShadow: isComplete
						? [
								"0 0 0 0 rgba(164, 61, 255, 0.6)",
								"0 0 .25rem 2rem rgba(164, 61, 255, 0)",
								"0 0 0 0 rgba(164, 61, 255, 0)",
							]
						: "",
				}}
				transition={{ duration: 0.8, ease: "linear" }}
			>
				<motion.div
					initial={{ opacity: 1, y: 0 }}
					animate={{
						opacity: isComplete ? 0 : 1,
						y: isComplete ? -10 : 0,
					}}
					transition={{
						duration: 0.3,
						ease: easeConfig,
						delay: isComplete ? 1 : 0,
					}}
				>
					<div
						className="w-full bg-[#F1F1F1] dark:bg-[#1B1B1B] rounded-xl overflow-hidden relative"
						style={props.style}
					>
						<motion.div
							initial={{ width: 0 }}
							animate={{ width: `${progress}%` }}
							transition={{ duration: 0.5, ease: easeConfig }}
							className="h-3 rounded-xl"
							style={{
								background: "linear-gradient(270deg, #A43DFF 4.26%, #5247E9 97.8%)",
							}}
						/>
					</div>

					<motion.div
						ref={lottieRef}
						initial={{ left: 0 }}
						animate={{ left: `${progress}%` }}
						transition={{ duration: 0.5, ease: easeConfig }}
						className={clsx(
							"w-[6.25rem] h-[6.25rem] absolute top-1/2  -translate-x-1/2 -translate-y-1/2",
							displayAnimation ? "block" : "hidden",
						)}
					/>
				</motion.div>
			</motion.div>
		</div>
	);
});

SurveyHeaderProgressBar.displayName = "SurveyHeaderProgressBar";
export default SurveyHeaderProgressBar;
